import { Metadata, OneDriveWebSocket } from '@samuraiexx/onedrive-incremental-sync';
import { v4 as uuidv4 } from 'uuid';
import * as Y from 'yjs'
import { getAccessToken } from './msalInstance';
import { IndexeddbPersistence } from 'y-indexeddb';
import { LocalPendingUpdateDb, PendingUpdate } from './pendingUpdateDb';
export class OneDriveProvider {
  private metadata: Metadata | undefined;
  private pendingUpdatesDb: LocalPendingUpdateDb;
  private indexDbProvider: IndexeddbPersistence;
  private ydoc: Y.Doc;
  private originId = uuidv4();
  private destroyed = false;

  private creatingOneDriveWebSocket = false;
  private oneDriveWebSocket: OneDriveWebSocket | undefined;

  private constructor(docName: string, ydoc: Y.Doc) {
    this.pendingUpdatesDb = new LocalPendingUpdateDb(docName);
    this.indexDbProvider = new IndexeddbPersistence(docName, ydoc);
    this.ydoc = ydoc;

    this.ydoc.on('updateV2', this.onUpdateV2);

    this.pendingUpdatesDb.onNewPendingUpdate(this.onPendingUpdate);
  }

  private onPendingUpdate = (_: number, pendingUpdate: PendingUpdate) => {
    // Using setTimeout to remove dexie transaction from the call stack
    setTimeout(async () => {
      try {
        if (this.oneDriveWebSocket !== undefined) {
          this.oneDriveWebSocket.pushUpdate(pendingUpdate.update);
          await this.oneDriveWebSocket.waitForProcessing();
          await this.pendingUpdatesDb.deletePendingUpdates([pendingUpdate.id]);
        }
      } catch (e) {
        console.error(e);
      }
    }, 0);
  }

  private onUpdateV2 = (update: Uint8Array, origin: string) => {
    if (origin === this.originId) {
      return;
    }
    this.pendingUpdatesDb.addPendingUpdate(update);
  }

  private getToken = async (): Promise<string> => {
    const accessToken = await getAccessToken(['Files.ReadWrite.All']);

    if (accessToken === undefined) {
      throw new Error('No access token');
    }

    return accessToken.accessToken;
  }

  static async getOneDriveProvider(docName: string, ydoc: Y.Doc): Promise<OneDriveProvider> {
    const oneDriveProvider = new OneDriveProvider(docName, ydoc);
    await oneDriveProvider.indexDbProvider.whenSynced;
    oneDriveProvider.metadata = await oneDriveProvider.indexDbProvider.get('metadata') as Metadata | undefined;

    return oneDriveProvider;
  }

  private getMetadata = () => this.metadata;

  private setMetadata = (metadata: Metadata) => {
    this.metadata = metadata;
    this.indexDbProvider.set('metadata', metadata);
  }

  tryInitOneDriveWebSocket = async () => {
    if (this.destroyed || this.oneDriveWebSocket !== undefined || this.creatingOneDriveWebSocket) {
      return;
    }
    this.creatingOneDriveWebSocket = true;

    try {
      this.oneDriveWebSocket = await OneDriveWebSocket.getOneDriveWebSocket(
        "Examinare/updates",
        this.getToken,
        this.getMetadata,
        this.setMetadata,
        data => Y.applyUpdateV2(this.ydoc, data, this.originId),
        Y.mergeUpdatesV2,
        () => Y.encodeStateAsUpdateV2(this.ydoc),
        200
      );

      await this.oneDriveWebSocket.waitForProcessing();

      const pendingUpdates = await this.pendingUpdatesDb.getPendingUpdates();
      const mergedUpdate = Y.mergeUpdatesV2(pendingUpdates.map(p => p.update));
      this.oneDriveWebSocket.pushUpdate(mergedUpdate);
      await this.oneDriveWebSocket.waitForProcessing();
      await this.pendingUpdatesDb.deletePendingUpdates(pendingUpdates.map(p => p.id));

    } catch (e) {
      console.error(e);
      this.oneDriveWebSocket?.disconnect();
      this.oneDriveWebSocket = undefined;
    }

    this.creatingOneDriveWebSocket = false;
  }

  disconnectWebsocket = () => {
    this.oneDriveWebSocket?.disconnect();
    this.oneDriveWebSocket = undefined;
  }

  destroy = () => {
    this.pendingUpdatesDb.offNewPendingUpdate(this.onPendingUpdate);
    this.ydoc.off('updateV2', this.onUpdateV2);
    this.indexDbProvider.destroy();
    this.pendingUpdatesDb.destroy();
    this.destroyed = true;
  }
}
